/**
 * @fileoverview WelcomeBanner - Greeting with quick action buttons on the dashboard
 * Shows a time-aware greeting (Good morning/afternoon/evening), user's first name,
 * and quick action buttons to create sessions or runs.
 */
import { Link } from 'react-router-dom'
import { Plus, Play } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/hooks/useAuth'

function getGreeting(): string {
  const hour = new Date().getHours()
  if (hour < 12) return 'Good morning'
  if (hour < 18) return 'Good afternoon'
  return 'Good evening'
}

function getFirstName(displayName: string): string {
  if (!displayName) return ''
  return displayName.split(' ')[0]
}

export interface WelcomeBannerProps {
  /** Override the active run count shown in the subtitle */
  activeRunCount?: number
  /** Override the active session count shown in the subtitle */
  activeSessionCount?: number
}

export function WelcomeBanner({ activeRunCount, activeSessionCount }: WelcomeBannerProps) {
  const { displayName } = useAuth()
  const firstName = getFirstName(displayName)
  const greeting = getGreeting()

  const parts: string[] = []
  if (activeRunCount !== undefined && activeRunCount > 0) {
    parts.push(`${activeRunCount} active run${activeRunCount !== 1 ? 's' : ''}`)
  }
  if (activeSessionCount !== undefined && activeSessionCount > 0) {
    parts.push(`${activeSessionCount} active session${activeSessionCount !== 1 ? 's' : ''}`)
  }
  const subtitle = parts.length > 0
    ? `You have ${parts.join(' and ')} right now.`
    : "Here's what's happening today."

  return (
    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
      <div className="space-y-1">
        <h1 className="text-2xl font-semibold tracking-tight">
          {greeting}{firstName ? `, ${firstName}` : ''}.
        </h1>
        <p className="text-sm text-muted-foreground">{subtitle}</p>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <Button asChild>
          <Link to="/orchestrate">
            <Plus className="h-4 w-4 mr-2" />
            New Session
          </Link>
        </Button>
        <Button variant="outline" asChild>
          <Link to="/assistant">
            <Play className="h-4 w-4 mr-2" />
            Assistant
          </Link>
        </Button>
      </div>
    </div>
  )
}
